import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAction, useMutation } from "convex/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  locationsGetById,
  posTerminalLocationsSetLocationTerminalLocation,
  stripeTerminalCreateLocation,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Spinner } from "~/components/ui/spinner";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

interface TerminalLocationFormState {
  displayName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

const EMPTY_TERMINAL_LOCATION_FORM: TerminalLocationFormState = {
  displayName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "US",
};

// V4.1: Route-based modal for URL context tracking
export const Route = createFileRoute(
  "/_authenticated/settings/terminals/setup/$locationId"
)({
  component: SetupTerminalModal,
});

function SetupTerminalModal() {
  const navigate = useNavigate();
  const { locationId } = useParams({
    from: "/_authenticated/settings/terminals/setup/$locationId",
  });

  const { data: location } = useQuery(
    convexQuery(locationsGetById, {
      id: toConvexTableId("locations", locationId),
    })
  );

  const createLocation = useAction(stripeTerminalCreateLocation);
  const setLocationTerminalLocation = useMutation(
    posTerminalLocationsSetLocationTerminalLocation
  );

  const [formData, setFormData] = useState<TerminalLocationFormState>(
    EMPTY_TERMINAL_LOCATION_FORM
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateFormField = <TKey extends keyof TerminalLocationFormState>(
    key: TKey,
    value: TerminalLocationFormState[TKey]
  ) => {
    setFormData((currentFormData) => ({
      ...currentFormData,
      [key]: value,
    }));
  };

  // Pre-populate form with location address
  useEffect(() => {
    if (location) {
      setFormData({
        displayName: location.name,
        line1: location.address?.addressLine1 ?? "",
        line2: location.address?.addressLine2 ?? "",
        city: location.address?.city ?? "",
        state: location.address?.state ?? "",
        postalCode: location.address?.zipCode ?? "",
        country: "US",
      });
    }
  }, [location]);

  const handleClose = () => {
    void navigate({ to: "/settings/terminals" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Step 1: Create the Stripe Terminal Location
      const terminalLocation = await createLocation({
        displayName: formData.displayName,
        address: {
          line1: formData.line1,
          line2: formData.line2 || undefined,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
        },
      });

      // Step 2: Link the Stripe Terminal location to the app location
      await setLocationTerminalLocation({
        locationId: toConvexTableId("locations", locationId),
        stripeTerminalLocationId: terminalLocation.id,
      });

      ProductEvents.terminalSetup(locationId);
      toast.success(
        `Terminal location created for ${location?.name ?? "location"}`
      );
      handleClose();
    } catch (error) {
      logger.exception("terminal_setup_failed", error, { locationId });
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to set up terminal location"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!location) {
    return (
      <Dialog onOpenChange={(open) => !open && handleClose()} open>
        <DialogContent className="sm:max-w-[500px]">
          <div className="flex items-center justify-center p-8">
            <Spinner className="size-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Stripe Terminal Location</DialogTitle>
          <DialogDescription>
            Create a Stripe Terminal Location for{" "}
            <strong>{location.name}</strong> to enable card reader registration.
            The address is pre-filled from the location profile.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="displayName">Location Name</Label>
              <Input
                id="displayName"
                onChange={(e) => {
                  updateFormField("displayName", e.target.value);
                }}
                placeholder="e.g., Main Street Store"
                required
                value={formData.displayName}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="line1">Address Line 1</Label>
              <Input
                id="line1"
                onChange={(e) => {
                  updateFormField("line1", e.target.value);
                }}
                placeholder="Street address"
                required
                value={formData.line1}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="line2">Address Line 2 (Optional)</Label>
              <Input
                id="line2"
                onChange={(e) => {
                  updateFormField("line2", e.target.value);
                }}
                placeholder="Apartment, suite, etc."
                value={formData.line2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  onChange={(e) => {
                    updateFormField("city", e.target.value);
                  }}
                  required
                  value={formData.city}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  onChange={(e) => {
                    updateFormField("state", e.target.value);
                  }}
                  placeholder="e.g., CA"
                  required
                  value={formData.state}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  onChange={(e) => {
                    updateFormField("postalCode", e.target.value);
                  }}
                  required
                  value={formData.postalCode}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="country">Country Code</Label>
                <Input
                  id="country"
                  onChange={(e) => {
                    updateFormField("country", e.target.value);
                  }}
                  placeholder="US"
                  required
                  value={formData.country}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              disabled={isSubmitting}
              onClick={handleClose}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2 size-4" />
                  Creating...
                </>
              ) : (
                "Create Terminal Location"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
